import { Star } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { MyAgent } from "@/api/types";
import { useAuth } from "@/auth/useAuth";
import { Button } from "@/components/ui/button";
import { RateAgentDialog } from "@/features/agents/components/RateAgentDialog";
import { useAgentRatings } from "@/features/agents/hooks/useAgents";
import { cn } from "@/lib/utils";

interface Props {
  agent: MyAgent;
  onOpenChat: (agentId: string) => void;
}

/**
 * Card de agente instalado (aba "Instalados" em /me/agents). Clique no
 * corpo abre o chat; botoes inferiores: "Abrir chat" e "Avaliar".
 *
 * O ``RateAgentDialog`` e renderizado FORA do <li> clicavel para que
 * cliques no dialog (estrelas, comentario) nao bubblem pelo React tree
 * ate o onClick do card. Mesmo com Portal do Radix, React Synthetic
 * Events bubblam pelo virtual DOM, nao pelo DOM real.
 */
export function InstalledAgentCard({ agent, onOpenChat }: Props) {
  const { t } = useTranslation("agents");
  const { user } = useAuth();
  const [rateOpen, setRateOpen] = useState(false);
  const ratings = useAgentRatings(rateOpen ? agent.id : "");
  const myRating =
    ratings.data?.items.find((r) => r.user_id === user?.id) ?? null;

  return (
    <>
      <li
        className="shadow-card flex cursor-pointer flex-col rounded-2xl border border-line bg-surface p-5 transition hover:-translate-y-0.5 hover:border-brand-300 hover:shadow-hover"
        onClick={() => onOpenChat(agent.id)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter") onOpenChat(agent.id);
        }}
      >
        {/* Nome a esquerda, rating a direita -- mantem altura do header
            consistente entre cards com e sem avaliacao. */}
        <div className="flex items-start justify-between gap-2">
          <h3 className="line-clamp-1 text-base font-semibold text-ink-950">
            {agent.name}
          </h3>
          {agent.rating_avg != null ? (
            <span className="inline-flex shrink-0 items-center gap-1 text-xs text-ink-500">
              <Star className="h-3 w-3 fill-accent-500 text-accent-500" />
              <span className="tabular">{agent.rating_avg.toFixed(1)}</span>
            </span>
          ) : null}
        </div>
        <p className="mt-1 line-clamp-2 text-sm text-ink-500">
          {agent.description}
        </p>
        <div
          className="mt-3 flex flex-wrap gap-2"
          onClick={(e) => e.stopPropagation()}
        >
          <Button size="sm" onClick={() => onOpenChat(agent.id)}>
            {t("list.openChat")}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              setRateOpen(true);
            }}
            className={cn(myRating && "border-brand-300 text-brand-700")}
          >
            <Star className="h-3 w-3" />
            {myRating ? t("list.editRating") : t("list.rate")}
          </Button>
        </div>
      </li>

      <RateAgentDialog
        agentId={agent.id}
        agentName={agent.name}
        open={rateOpen}
        onOpenChange={setRateOpen}
        existingRating={myRating}
      />
    </>
  );
}
